import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const formatMoney = (cents, format) => {
  const fmt = format || '${{amount}}';
  const value = (Math.round(cents) / 100).toFixed(2);
  const [int, dec] = value.split('.');
  const withSep = (sep) => int.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
  return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    switch (key) {
      case 'amount_no_decimals':
        return withSep(',');
      case 'amount_with_comma_separator':
        return `${withSep('.')},${dec}`;
      case 'amount_no_decimals_with_comma_separator':
        return withSep('.');
      case 'amount_with_apostrophe_separator':
        return `${withSep("'")}.${dec}`;
      default:
        return `${withSep(',')}.${dec}`;
    }
  });
};

class MnBundle extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = 'true';

    try {
      this.variants = JSON.parse(this.querySelector('[data-mn-variants]').textContent);
    } catch (e) {
      this.variants = [];
    }

    this.duoPercent = Number(this.dataset.duoPercent) || 0;
    this.moneyFormat = this.dataset.moneyFormat;

    this.addEventListener('change', (event) => {
      const target = event.target;
      if (target.matches('.mn-bundle__radio')) this.#selectOption(target.value);
      if (target.matches('.mn-bundle__select')) this.#onSelectChange(target);
    });

    this.#selectOption(this.querySelector('.mn-bundle__radio:checked')?.value || this.dataset.default || 'single');
    this.#updatePrices();

    this.boundSubmit = (event) => this.#onSubmit(event);
    window.addEventListener('submit', this.boundSubmit, true);
  }

  disconnectedCallback() {
    if (this.boundSubmit) window.removeEventListener('submit', this.boundSubmit, true);
    delete this.dataset.ready;
  }

  get section() {
    return this.closest('.shopify-section') || document;
  }

  get selectedOption() {
    return this.querySelector('.mn-bundle__option.is-selected');
  }

  #selectOption(key) {
    this.querySelectorAll('.mn-bundle__option').forEach((option) => {
      const selected = option.dataset.option === key;
      option.classList.toggle('is-selected', selected);
      const radio = option.querySelector('.mn-bundle__radio');
      if (radio) radio.checked = selected;
      const variants = option.querySelector('.mn-bundle__variants');
      if (variants) variants.hidden = !selected;
    });
    const firstRow = this.selectedOption?.querySelector('[data-mn-row]');
    if (firstRow) this.#syncGallery(this.#variantForRow(firstRow));
  }

  #variantForRow(row) {
    const values = Array.from(row.querySelectorAll('.mn-bundle__select')).map((s) => s.value);
    if (!values.length) return this.variants[0];
    return this.variants.find((v) => values.every((val, i) => v.options[i] === val));
  }

  #onSelectChange(select) {
    const row = select.closest('[data-mn-row]');
    const variant = this.#variantForRow(row);
    const thumb = row.querySelector('[data-mn-row-thumb]');
    if (variant && thumb && variant.image && thumb.tagName === "IMG") thumb.src = variant.image;
    this.#updatePrices();
    this.#syncGallery(variant);
  }

  #syncGallery(variant) {
    if (!variant || !variant.image_key) return;
    try {
      const slideshow = this.section.querySelector('media-gallery slideshow-component');
      if (!slideshow || typeof slideshow.select !== 'function') return;
      const slides = Array.from(slideshow.querySelectorAll('slideshow-slide'));
      const index = slides.findIndex((slide) => {
        const img = slide.querySelector('img');
        return img && (img.currentSrc || img.src || '').includes(variant.image_key);
      });
      if (index > -1) slideshow.select(index);
    } catch (e) {
      /* gallery sync is best effort */
    }
  }

  #updatePrices() {
    this.querySelectorAll('.mn-bundle__option').forEach((option) => {
      const rows = Array.from(option.querySelectorAll('[data-mn-row]'));
      const variants = rows.map((row) => this.#variantForRow(row)).filter(Boolean);
      if (!variants.length) return;
      const isDuo = option.dataset.option === 'duo' && this.duoPercent;
      const price = variants.reduce(
        (sum, v) => sum + (isDuo ? v.price - Math.floor((v.price * this.duoPercent) / 100) : v.price),
        0
      );
      const compare = variants.reduce((sum, v) => sum + Math.max(v.compare_at_price || 0, v.price), 0);
      const priceEl = option.querySelector('[data-mn-price]');
      const compareEl = option.querySelector('[data-mn-compare]');
      if (priceEl) priceEl.textContent = formatMoney(price, this.moneyFormat);
      if (compareEl) {
        compareEl.textContent = formatMoney(compare, this.moneyFormat);
        compareEl.hidden = compare <= price;
      }
    });
  }

  #selectedItems() {
    const option = this.selectedOption;
    if (!option) return [];
    const counts = new Map();
    option.querySelectorAll('[data-mn-row]').forEach((row) => {
      const variant = this.#variantForRow(row);
      if (variant) counts.set(variant.id, (counts.get(variant.id) || 0) + 1);
    });
    return Array.from(counts, ([id, quantity]) => ({ id, quantity }));
  }

  async #onSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.closest('product-form-component')) return;
    if (this.section !== document && !this.section.contains(form)) return;

    const items = this.#selectedItems();
    if (!items.length) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const unavailable = items.find((item) => !this.variants.find((v) => v.id === item.id)?.available);
    const productForm = form.closest('product-form-component');
    const button = form.querySelector('[ref="addToCartButton"], button[type="submit"]');

    if (unavailable) {
      productForm.dispatchEvent(new CartErrorEvent({ error: 'This combination is sold out', code: 'INVALID' }));
      return;
    }

    button?.setAttribute('aria-busy', 'true');
    button?.classList.add('mn-loading');

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => el.dataset.sectionId)
      .filter(Boolean)
      .join(',');

    const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
    const deferred = CartLinesUpdateEvent.createPromise();

    productForm.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const isDuo = this.selectedOption?.dataset.option === 'duo';
    const code = (this.dataset.discountCode || '').trim();

    try {
      const addResponse = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: isDuo && code ? undefined : sectionIds }),
      }).then((r) => r.json());

      if (addResponse.status) throw new Error(addResponse.description || addResponse.message || 'Add to cart failed');

      let sections = addResponse.sections;

      if (isDuo && code) {
        const current = await fetch(`${root}cart.js`).then((r) => r.json());
        const existing = (current.discount_codes || []).map((d) => d.code).filter((c) => c && c !== code);
        const update = await fetch(`${root}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ discount: [...existing, code].join(','), sections: sectionIds }),
        }).then((r) => r.json());
        sections = update.sections || sections;
      }

      const cart = await fetch(`${root}cart.js`).then((r) => r.json());

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'product-form-component',
          sourceId: productForm.id ? productForm.id.toString() : 'mn-bundle',
          itemCount: totalQuantity,
          productId: productForm.dataset.productId,
          sections,
          didError: false,
        },
      });
    } catch (error) {
      console.error(error);
      deferred.reject(error);
      productForm.dispatchEvent(new CartErrorEvent({ error: error.message, code: 'INVALID' }));
    } finally {
      button?.removeAttribute('aria-busy');
      button?.classList.remove('mn-loading');
    }
  }
}

if (!customElements.get('mn-bundle')) customElements.define('mn-bundle', MnBundle);
